import { type ChangeEvent, type CSSProperties, useCallback } from 'react';
import type { Lens } from '@hookform/lenses';
import { useFormContext, useWatch } from 'react-hook-form';
import { z } from 'zod/v4';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { PlainIconButton } from '@interxfi/design-system/components/button';
import { SwitchInput } from '@interxfi/design-system/components/input';
import { Tooltip } from '@interxfi/design-system/components/tooltip/tooltip';
import { Typography } from '@interxfi/design-system/components/typography';
import InfoIcon from '@interxfi/design-system/icons/react/icon-info';
import type { IxcRegistrationV1Address, IxmUserauthV1CustomerSupportContacts } from '@interxfi/types';
import { isDeepEqual } from '@interxfi/utilities/equality/deep';
import { AppTextInputWithLens } from '../../../../components/form/app-text-input';
import { addDefinedValues } from '../../../../util/has-value';
import {
  AddressForm,
  type AddressFormLabels,
  addressFormSchema,
  defaultAddressFormValues,
  formValuesToAddress,
  getInitialFormValuesForAddress,
  STACKED_ADDRESS_FORM_LAYOUT,
} from '../../../registration/shared-form/address/address-form';

export const programCustomerSupportContactFormSchema = z.object({
  displayName: z.string(),
  website: z
    .string()
    .regex(/^https?:\/\//, { message: 'Website must be a valid URL, starting with http:// or https://' })
    .pipe(
      z.url({
        protocol: /^https?$/,
        hostname: z.regexes.domain,
        error: 'Website must be a valid URL, starting with http:// or https://',
      }),
    )
    .or(z.literal('')),
  emailAddress: z.email({ error: 'Invalid email address' }).or(z.literal('')),
  phone: z.string(),
  phoneTaxDocuments: z.string().nonempty({ error: 'Phone Number for Tax Forms is required' }),
  mailingAddress: z.object({
    isSameAsMainOffice: z.boolean(),
    value: addressFormSchema.optional(),
  }),
});

export const onSubmitProgramCustomerSupportContactFormSchema = programCustomerSupportContactFormSchema.refine(
  (value) => value.emailAddress.length > 0 || value.phone.length > 0 || value.website.length > 0,
  {
    error: 'Customer Support Contact must have at least one of the following: email address, website, or phone.',
  },
);

export type ProgramCustomerSupportContactFormValues = z.infer<typeof programCustomerSupportContactFormSchema>;

export const defaultProgramCustomerSupportContactFormValues: ProgramCustomerSupportContactFormValues = {
  displayName: '',
  website: '',
  emailAddress: '',
  phone: '',
  phoneTaxDocuments: '',
  mailingAddress: {
    isSameAsMainOffice: false,
    value: defaultAddressFormValues,
  },
};

export function getInitialFormValuesForProgramCustomerSupportContact(
  data: IxmUserauthV1CustomerSupportContacts | undefined,
  mainOfficeAddress: IxcRegistrationV1Address | undefined,
): ProgramCustomerSupportContactFormValues {
  if (!data) {
    return defaultProgramCustomerSupportContactFormValues;
  }

  const isMailingAddressSameAsMainOffice = Boolean(mainOfficeAddress && isDeepEqual(mainOfficeAddress, data?.mailingAddress));

  return {
    displayName: data.displayName || '',
    website: data.website || '',
    emailAddress: data.emailAddress || '',
    phone: data.phone || '',
    phoneTaxDocuments: data.phoneTaxDocuments || '',
    mailingAddress: {
      isSameAsMainOffice: isMailingAddressSameAsMainOffice,
      value: isMailingAddressSameAsMainOffice ? undefined : getInitialFormValuesForAddress(data.mailingAddress) || defaultAddressFormValues,
    },
  };
}

export function formValuesToProgramCustomerSupportContact(
  data: ProgramCustomerSupportContactFormValues,
  programName: string,
  mainOfficeAddress: IxcRegistrationV1Address,
): IxmUserauthV1CustomerSupportContacts {
  return addDefinedValues<IxmUserauthV1CustomerSupportContacts>(
    {
      displayName: data.displayName || programName,
      phoneTaxDocuments: data.phoneTaxDocuments,
      mailingAddress:
        !data.mailingAddress.isSameAsMainOffice && data.mailingAddress.value ? formValuesToAddress(data.mailingAddress.value) : mainOfficeAddress,
    },
    {
      website: data.website || undefined,
      emailAddress: data.emailAddress || undefined,
      phone: data.phone || undefined,
    },
  );
}

const MAILING_ADDRESS_LABELS: AddressFormLabels = {
  usAddress: {
    street1: 'Mailing Address',
  },
};

const headingTooltipActivatorStyle = {
  marginRight: 16,
} as const satisfies CSSProperties;

interface ProgramCustomerSupportContactFormProps {
  lens: Lens<ProgramCustomerSupportContactFormValues>;
  showTooltips?: boolean;
}

export function ProgramCustomerSupportContactForm({ lens, showTooltips }: ProgramCustomerSupportContactFormProps) {
  const { setValue, unregister } = useFormContext();
  const isUsingMainOfficeAddress = useWatch({ ...lens.focus('mailingAddress.isSameAsMainOffice').interop(), defaultValue: true });
  const mailingAddressName = lens.focus('mailingAddress').interop().name;
  const mailingAddressValueName = lens.focus('mailingAddress.value').interop().name;
  const handleToggleProvidingMailingAddress = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newIsChecked = e.target.checked;

      if (!newIsChecked) {
        unregister(mailingAddressValueName);
      }

      setValue(
        mailingAddressName,
        {
          isSameAsMainOffice: newIsChecked,
          value: newIsChecked ? undefined : defaultAddressFormValues,
        },
        {
          shouldValidate: true,
        },
      );
    },
    [unregister, mailingAddressValueName, setValue, mailingAddressName],
  );

  return (
    <VerticalStack gap={7}>
      <VerticalStack gap={4}>
        <HorizontalStack align="space-between" blockAlign="center" gap={4} wrap="nowrap">
          <Typography.Level3>Customer Support Contact</Typography.Level3>

          {showTooltips ? (
            <Tooltip
              activator={
                <PlainIconButton style={headingTooltipActivatorStyle}>
                  <InfoIcon aria-hidden height={16} />
                </PlainIconButton>
              }
              maxWidth={220}
              side="top"
              size="small"
            >
              Appears on Statements and Trade Confirmations as a contact for customer inquiries.
            </Tooltip>
          ) : null}
        </HorizontalStack>

        {/* This field is technically required by the API. If it's not provided, the full program name will be used instead. */}
        <AppTextInputWithLens
          info={
            showTooltips
              ? 'Appears on all customer-facing documents. If the Program’s logo does not clearly identify the introducing company, a Program Display Name is required.'
              : undefined
          }
          infoWidth={220}
          label="Program Display Name"
          lens={lens.focus('displayName')}
        />
        <AppTextInputWithLens label="Support Website" lens={lens.focus('website')} />
        <AppTextInputWithLens autoComplete="off" label="Email Address" lens={lens.focus('emailAddress')} />
        <AppTextInputWithLens autoComplete="off" label="Phone" lens={lens.focus('phone')} />
        <AppTextInputWithLens
          autoComplete="off"
          info={
            showTooltips ? 'Appears on all tax forms per rigid IRS rules with the intent that it will be used for customer inquiries.' : undefined
          }
          label="Phone (Tax Forms)"
          lens={lens.focus('phoneTaxDocuments')}
          required
        />
      </VerticalStack>

      <VerticalStack gap={4}>
        <HorizontalStack align="space-between" blockAlign="center" gap={4} wrap="nowrap">
          <Typography.Level3>Customer Support Contact Mailing Address</Typography.Level3>

          {showTooltips ? (
            <Tooltip
              activator={
                <PlainIconButton style={headingTooltipActivatorStyle}>
                  <InfoIcon aria-hidden height={16} />
                </PlainIconButton>
              }
              maxWidth={220}
              side="top"
              size="small"
            >
              Appears on all customer-facing documents and may be used for customer inquiries. It will also serve as the return address on physical
              shareholder or proxy materials, including any undeliverable mail.
            </Tooltip>
          ) : null}
        </HorizontalStack>

        <SwitchInput
          checked={isUsingMainOfficeAddress}
          label="Same as main office address"
          name={lens.focus('mailingAddress.isSameAsMainOffice').interop().name}
          onChange={handleToggleProvidingMailingAddress}
        />

        {isUsingMainOfficeAddress ? null : (
          <AddressForm labels={MAILING_ADDRESS_LABELS} layout={STACKED_ADDRESS_FORM_LAYOUT} lens={lens.focus('mailingAddress.value').narrow()} />
        )}
      </VerticalStack>
    </VerticalStack>
  );
}
